import { POS_REPORT_HIDDEN_FIELDS, POS_REPORTS, reportColumnLabel } from '@iws/backoffice-api'
import { Download, FileSpreadsheet } from 'lucide-react'

import { useMemo, useState } from 'react'

import { Input, Label, PosButton } from '@/components/ui'
import { PageHeader } from '@/components/pos/PageHeader'
import { NativeSelect } from '@/components/pos/office/NativeSelect'
import { NeedsConnection } from '@/components/pos/office/NeedsConnection'
import { OfficeTable } from '@/components/pos/office/OfficeTable'
import { useSession } from '@/contexts/SessionContext'
import { useProducts } from '@/hooks/features/catalog'
import { useOfficeQuery, useOnline } from '@/hooks/features/office'
import { OfficeModel } from '@/http/models'
import { downloadCsv } from '@/lib/csv'
import { downloadXlsx } from '@/lib/xlsx'
import { daysAgo, ymd } from '@/lib/dates'
import { formatMoney } from '@/utils'

type Row = Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any

interface ReportDef {
  key: string
  label: string
  permission: string
  /** `pos-report/<path>`; P&L reads `profit-loss`. */
  path?: string
  dated?: boolean
}

/** P&L and the seven POS reports (B17), each behind its own key (POS.md §18.3). */
const REPORTS: ReportDef[] = [
  { key: 'pl', label: 'Profit & loss by day', permission: 'pos:view_profit_loss' },
  { key: 'pl_product', label: 'Profit & loss by product', permission: 'pos:view_profit_loss' },
  ...POS_REPORTS.map(({ key, label, permission, path, dated }) => ({ key, label, permission, path, dated })),
]

const HIDDEN = new Set(POS_REPORT_HIDDEN_FIELDS)
const label = reportColumnLabel
const show = (v: unknown) =>
  v == null ? '' : typeof v === 'number' ? String(Math.round(v * 100) / 100) : typeof v === 'object' ? JSON.stringify(v) : String(v)

/** This branch's reports from the server, with a CSV download (opens in Excel). */
export const ReportsPage = () => {
  const { can, device } = useSession()
  const online = useOnline()
  const allowed = REPORTS.filter((r) => can(r.permission))
  const [key, setKey] = useState(allowed[0]?.key ?? '')
  const [from, setFrom] = useState(daysAgo(29))
  const [to, setTo] = useState(ymd(new Date()))
  const report = allowed.find((r) => r.key === key) ?? allowed[0]
  const branchId = device?.branch_id ?? ''
  const range = { branch_id: branchId, from_date: from, to_date: to }
  const { data: products } = useProducts({})

  const { data, isFetching } = useOfficeQuery(
    ['report', report?.key, from, to],
    () =>
      report.key === 'pl'
        ? OfficeModel.profitLoss(range)
        : report.key === 'pl_product'
          ? OfficeModel.profitLossByProduct(range)
          : report.key === 'shifts'
            ? OfficeModel.shiftReports({ branch_id: branchId, page: 1 }).then((res) => ({ ...res, data: res.data && { result: res.data.result } }))
            : OfficeModel.report(report.path!, { ...range, page: 1, length: 500 }),
    !!report
  )

  const nameOf = useMemo(() => {
    const map = new Map((products?.items ?? []).map((p) => [p._id, p.name]))
    return (id: unknown) => map.get(String(id)) ?? String(id ?? '')
  }, [products])

  const table = useMemo(() => {
    const list: Row[] = data?.result ?? []
    if (report?.key === 'pl' || report?.key === 'pl_product') {
      const rows: Row[] = list.map((r) => ({
        ...(report.key === 'pl_product' ? { product: nameOf(r.product_variation_id) } : {}),
        date: r.date,
        revenue: Number(r.sales_revenue ?? 0),
        cost: Number(r.purchase_cost ?? 0),
        gross_profit: Number(r.sales_revenue ?? 0) - Number(r.purchase_cost ?? 0),
      }))
      return { keys: rows[0] ? Object.keys(rows[0]) : ['date', 'revenue', 'cost', 'gross_profit'], rows }
    }
    const keys = [...new Set(list.flatMap((r) => Object.keys(r)))].filter((k) => !HIDDEN.has(k))
    return { keys, rows: list }
  }, [data, report, nameOf]) as { keys: string[]; rows: Row[] }

  const money = (k: string) => ['revenue', 'cost', 'gross_profit'].includes(k)
  const totals = report?.key.startsWith('pl')
    ? (['revenue', 'cost', 'gross_profit'] as const).map((k) => [k, table.rows.reduce((s, r) => s + Number(r[k] ?? 0), 0)] as const)
    : []

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Reports" subtitle="This branch, from the server.">
        <PosButton
          variant="outline"
          disabled={!table.rows.length}
          onClick={() => downloadCsv(`${report.key}-${from}-${to}`, table.keys.map(label), table.rows.map((r) => table.keys.map((k) => show(r[k]))))}
        >
          <Download /> CSV
        </PosButton>
        <PosButton
          variant="outline"
          disabled={!table.rows.length}
          onClick={() =>
            downloadXlsx(
              `${report.key}-${from}-${to}`,
              table.keys.map(label),
              table.rows.map((r) => table.keys.map((k) => (typeof r[k] === 'number' ? r[k] : show(r[k])))),
              report.label
            )
          }
        >
          <FileSpreadsheet /> Excel
        </PosButton>
      </PageHeader>
      <div className="flex flex-wrap items-end gap-3 border-b border-border px-4 py-3 md:px-6">
        <div className="grid gap-1.5">
          <Label htmlFor="report">Report</Label>
          <NativeSelect id="report" className="w-60" value={report?.key ?? ''} onChange={(e) => setKey(e.target.value)} options={allowed.map((r) => ({ value: r.key, label: r.label }))} />
        </div>
        {report?.dated !== false && (
          <>
            <div className="grid gap-1.5">
              <Label htmlFor="from">From</Label>
              <Input id="from" type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="h-11" />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="to">To</Label>
              <Input id="to" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="h-11" />
            </div>
          </>
        )}
      </div>
      {!online ? (
        <NeedsConnection />
      ) : (
        <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4 md:p-6">
          {totals.length > 0 && (
            <div className="flex flex-wrap gap-6 text-sm">
              {totals.map(([k, v]) => (
                <span key={k}>
                  <span className="text-muted-foreground">{label(k)}: </span>
                  <span className="font-semibold tabular-nums text-ink">{formatMoney(v)}</span>
                </span>
              ))}
            </div>
          )}
          <OfficeTable
            loading={isFetching}
            rows={table.rows}
            rowKey={(r, i) => String(r._id ?? `${r.date}-${r.product ?? ''}-${i}`)}
            empty="No figures for this period."
            columns={table.keys.map((k) => ({
              label: label(k),
              className: money(k) || typeof table.rows[0]?.[k] === 'number' ? 'text-right tabular-nums' : undefined,
              cell: (r: Row) => (money(k) ? formatMoney(Number(r[k] ?? 0)) : show(r[k])),
            }))}
          />
        </div>
      )}
    </div>
  )
}
